/**
 * O CAMPO "ESCOLA" DO CADASTRO, ESCOLHIDO DA BASE OFICIAL — T18.9, regras de 06/10 e 08/10.
 *
 * "Ninguém digita nome de instituição: escolhe de uma lista oficial." E o vínculo funciona pelas
 * DUAS vias (08/10): logo após o cadastro, e depois, em Minha conta. Este campo é a primeira via —
 * a escola escolhida aqui vira o pedido de vínculo assim que a conta é criada.
 */
import { useEffect, useState } from "react";
import { InstituicaoDaBase } from "./api";
import { BaseDeEscolas, carregarBase } from "./base-de-escolas";

interface Props {
  id: string;
  /** O nome que já está no perfil (contas antigas podem ter texto livre: continua valendo). */
  valorAtual: string;
  desabilitado?: boolean;
  aoEscolher: (escola: InstituicaoDaBase | null) => void;
}

export default function EscolherEscola({ id, valorAtual, desabilitado, aoEscolher }: Props) {
  const [base, setBase] = useState<BaseDeEscolas | null>(null);
  const [texto, setTexto] = useState(valorAtual);
  const [aberto, setAberto] = useState(false);
  const [achados, setAchados] = useState<InstituicaoDaBase[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => setTexto(valorAtual), [valorAtual]);

  useEffect(() => {
    if (!aberto || base || texto.trim().length < 3) return;
    carregarBase()
      .then(setBase)
      .catch((e: unknown) => setErro(e instanceof Error ? e.message : String(e)));
  }, [aberto, texto, base]);

  useEffect(() => {
    if (!base || texto.trim().length < 3) {
      setAchados([]);
      return;
    }
    setAchados(base.buscar(texto, { limite: 8 }));
  }, [base, texto]);

  return (
    <div className="escolher-escola">
      {/* T26.R1 · SEM `required`. O rótulo diz «Escola (opcional)» e a letra do T2.68 diz «não é
          obrigatório»; o `required` que estava aqui travava o envio calado, com o balão do
          navegador em inglês (relatório de 09/10: a pessoa escolheu escola de outra cidade só para
          destravar). Sem escola, a conta nasce sem vínculo — e o vínculo se pede depois. */}
      <input
        id={id}
        autoComplete="off"
        disabled={desabilitado}
        maxLength={120}
        value={texto}
        placeholder="Comece a digitar o nome da escola ou a cidade"
        aria-autocomplete="list"
        aria-controls={`${id}-lista`}
        onFocus={() => setAberto(true)}
        onChange={(e) => {
          setTexto(e.target.value.trimStart());
          setAberto(true);
          // Mudou o texto: a escolha anterior deixa de valer até escolher de novo da lista.
          aoEscolher(null);
        }}
      />
      {erro && <span className="campo-erro" role="alert">{erro}</span>}
      {aberto && achados.length > 0 && (
        <ul id={`${id}-lista`} role="listbox" className="escolher-escola-lista" data-testid="lista-base-cadastro">
          {achados.map((a) => (
            <li key={a.id} role="option" aria-selected={false}>
              <button
                type="button"
                onClick={() => {
                  setTexto(a.nome);
                  setAberto(false);
                  aoEscolher(a);
                }}
              >
                <strong>{a.nome}</strong>
                <span>
                  {a.municipio} · {a.nivel === "superior" ? "ensino superior" : "educação básica"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <span className="campo-ajuda">
        A lista é a oficial do INEP. Escolha a sua escola nela: ao criar a conta, o pedido para entrar
        nela já vai junto.
      </span>
    </div>
  );
}
